import { useEffect } from "react";
import { Link } from "react-router-dom";
import UniversalLayout from "./components/UniversalLayout";
import "./LegalPage.css";

const LEGAL_CONTACT_EMAIL = "support@latted.com";
const LAST_UPDATED = "June 18, 2026";

const PRIVACY_SECTIONS = [
  {
    id: "information-we-collect",
    title: "Information We Collect",
    body: [
      "We collect information you provide directly to us when you create an account, build projects, upload assets, communicate with us, or otherwise use Latted (the “Service”). This may include:",
      {
        list: [
          "Account information, such as your name, email address, and authentication credentials.",
          "Content you create or upload, including prompts, images, videos, audio, documents, and project files.",
          "Optional face data, only when you choose to upload or submit an image, video frame, or other visual reference that contains a human face for AI content generation, editing, or personalization.",
          "Payment and billing information, processed securely by our third-party payment providers.",
          "Communications you send to us, such as support requests and feedback.",
        ],
      },
      "We also collect certain information automatically when you use the Service, including device and browser information, IP address, usage data, and information collected through cookies and similar technologies.",
    ],
  },
  {
    id: "how-we-use-information",
    title: "How We Use Your Information",
    body: [
      "We use the information we collect to:",
      {
        list: [
          "Provide, maintain, and improve the Service, including generating and rendering your video projects.",
          "Process optional visual references, including images or video frames that may contain faces, to generate, edit, animate, or personalize the content you request.",
          "Process transactions and manage your account, subscriptions, and credits.",
          "Communicate with you about products, services, updates, and promotional offers.",
          "Monitor and analyze trends, usage, and activity to improve the Service.",
          "Detect, investigate, and prevent fraud, abuse, and other harmful activity.",
          "Comply with legal obligations and enforce our agreements.",
        ],
      },
    ],
  },
  {
    id: "face-data",
    title: "Face Data",
    body: [
      "Face data is optional. You may use Latted without submitting images, videos, or visual references that contain a face. If you choose to submit face data, we collect only the face image or face-containing visual reference you provide, such as an uploaded photo, video frame, or project asset. We do not collect face geometry, faceprints, biometric identifiers, facial recognition templates, or information used to identify or authenticate a person.",
      "We use optional face data only to provide the AI-powered creation features you request, such as generating, editing, animating, or personalizing images and videos based on the submitted reference. We do not use face data to identify you or another person, authenticate identity, track users, build biometric profiles, or sell advertising.",
      "Face data may be shared with third-party AI model providers solely as needed to process the generation, editing, animation, or personalization request you initiate. We use AI model providers with zero data retention policies for these requests. Those providers are not permitted to retain the face data after processing or use it to train their models.",
      "Latted does not retain submitted face images or face-containing visual references when they are shared with AI model providers for processing. Temporary processing copies may exist only as needed to complete the requested generation or editing workflow and are deleted after processing completes, unless you separately choose to save the resulting project content or upload the same content as a project asset.",
    ],
  },
  {
    id: "how-we-share-information",
    title: "How We Share Your Information",
    body: [
      "We do not sell your personal information. We may share your information in the following circumstances:",
      {
        list: [
          "With service providers and vendors who perform services on our behalf, such as cloud hosting, payment processing, analytics, and customer support.",
          "With AI model providers, solely to the extent necessary to process your prompts and generate the content you request.",
          "With third-party AI model providers that maintain zero data retention policies when you choose to submit face data for AI generation, editing, animation, or personalization.",
          "In connection with a merger, acquisition, financing, or sale of assets, where information may be transferred as part of the transaction.",
          "When required by law, legal process, or to protect the rights, property, and safety of Latted, our users, or others.",
          "With your consent or at your direction, such as when you choose to publish or share a project.",
        ],
      },
    ],
  },
  {
    id: "cookies",
    title: "Cookies and Tracking Technologies",
    body: [
      "We and our partners use cookies, pixels, and similar technologies to operate the Service, remember your preferences, analyze usage, and measure the effectiveness of marketing campaigns. You can configure your browser to refuse cookies or alert you when cookies are being set; however, some features of the Service may not function properly without them.",
    ],
  },
  {
    id: "data-retention",
    title: "Data Retention",
    body: [
      "We retain your information for as long as your account is active or as needed to provide the Service, comply with our legal obligations, resolve disputes, and enforce our agreements. You may request deletion of your account and associated data at any time by contacting us; we will honor such requests subject to applicable legal retention requirements.",
      "We do not retain submitted face images or face-containing visual references when they are shared with AI model providers for processing. Temporary processing copies are deleted after the requested generation or editing workflow completes. We may retain project outputs or assets only if you choose to save them to your account, and you may request deletion of those saved materials.",
    ],
  },
  {
    id: "security",
    title: "Security",
    body: [
      "We implement reasonable technical and organizational measures designed to protect your information against unauthorized access, loss, misuse, or alteration. However, no method of transmission over the internet or method of electronic storage is completely secure, and we cannot guarantee absolute security.",
    ],
  },
  {
    id: "your-rights",
    title: "Your Rights and Choices",
    body: [
      "Depending on where you live, you may have rights regarding your personal information, including the right to access, correct, delete, or export your data, and the right to object to or restrict certain processing. You can exercise these rights by contacting us at the address below. You may also opt out of marketing emails at any time by using the unsubscribe link in those messages.",
      "Submitting face data is optional. You can choose not to upload images, videos, or visual references that contain a face. If you have saved project assets or outputs that contain a face, you may delete them through the Service where available or contact us to request deletion.",
    ],
  },
  {
    id: "childrens-privacy",
    title: "Children's Privacy",
    body: [
      "The Service is not directed to children under the age of 13 (or the equivalent minimum age in your jurisdiction), and we do not knowingly collect personal information from children. If we learn that we have collected personal information from a child, we will take steps to delete it promptly.",
    ],
  },
  {
    id: "international-transfers",
    title: "International Data Transfers",
    body: [
      "We may process and store your information in countries other than your own, including the United States. Where required, we use appropriate safeguards — such as standard contractual clauses — to protect information transferred internationally.",
    ],
  },
  {
    id: "changes",
    title: "Changes to This Policy",
    body: [
      "We may update this Privacy Policy from time to time. If we make material changes, we will notify you by updating the date at the top of this page and, where appropriate, by providing additional notice through the Service or by email. Your continued use of the Service after changes become effective constitutes acceptance of the revised policy.",
    ],
  },
  {
    id: "contact",
    title: "Contact Us",
    body: [
      `If you have any questions about this Privacy Policy or our data practices, please contact us at ${LEGAL_CONTACT_EMAIL}.`,
    ],
  },
];

const TERMS_SECTIONS = [
  {
    id: "acceptance",
    title: "Acceptance of Terms",
    body: [
      "These Terms of Service (“Terms”) govern your access to and use of Latted (the “Service”), operated by Latted (“we,” “us,” or “our”). By accessing or using the Service, you agree to be bound by these Terms and our Privacy Policy. If you do not agree to these Terms, you may not use the Service.",
      "If you are using the Service on behalf of an organization, you represent that you have the authority to bind that organization to these Terms, and “you” refers to that organization.",
    ],
  },
  {
    id: "description",
    title: "Description of the Service",
    body: [
      "Latted is an AI-powered video creation platform that lets you generate, edit, automate, and publish video content from prompts and reference materials. The Service is provided on an as-available basis and may change over time as we add, modify, or remove features.",
    ],
  },
  {
    id: "accounts",
    title: "Accounts and Registration",
    body: [
      "To use most features of the Service you must create an account. You agree to provide accurate and complete information, keep your credentials secure, and notify us promptly of any unauthorized use of your account. You are responsible for all activity that occurs under your account.",
    ],
  },
  {
    id: "user-content",
    title: "User Content and Licenses",
    body: [
      "You retain ownership of the prompts, assets, and other materials you upload to the Service (“User Content”) and, to the extent permitted by applicable law, the video content you generate with the Service (“Output”).",
      "By uploading User Content, you grant us a worldwide, non-exclusive, royalty-free license to host, store, reproduce, process, and display that content solely as necessary to operate, provide, and improve the Service. You represent and warrant that you own or have the necessary rights to all User Content you submit, and that it does not infringe the rights of any third party.",
    ],
  },
  {
    id: "acceptable-use",
    title: "Acceptable Use",
    body: [
      "You agree not to use the Service to:",
      {
        list: [
          "Violate any applicable law or regulation, or infringe the intellectual property, privacy, or publicity rights of others.",
          "Create or distribute content that is unlawful, defamatory, harassing, hateful, or sexually exploitative, or that depicts real individuals in a misleading or harmful manner without their consent.",
          "Misrepresent AI-generated content as authentic footage where doing so could deceive or cause harm.",
          "Attempt to probe, scan, disrupt, or gain unauthorized access to the Service or its related systems.",
          "Resell, sublicense, or scrape the Service, or use it to build a competing product.",
        ],
      },
      "We reserve the right to remove content and suspend or terminate accounts that violate these rules.",
    ],
  },
  {
    id: "payments",
    title: "Payments, Credits, and Billing",
    body: [
      "Certain features of the Service require payment, whether through subscriptions, usage-based credits, or other plans. Prices are displayed before purchase and may change with notice. Unless otherwise stated or required by law, all fees are non-refundable, and unused credits expire as described at the time of purchase. You authorize us and our payment processors to charge your selected payment method for all amounts due.",
    ],
  },
  {
    id: "intellectual-property",
    title: "Intellectual Property",
    body: [
      "The Service, including its software, design, branding, and all related intellectual property, is owned by Latted or its licensors and is protected by applicable laws. Except for the limited rights expressly granted to you under these Terms, no rights in the Service are transferred to you.",
    ],
  },
  {
    id: "third-party",
    title: "Third-Party Services",
    body: [
      "The Service may integrate with or rely on third-party services, including AI model providers and social media platforms. Your use of those services is subject to their own terms and policies, and we are not responsible for third-party services or content.",
    ],
  },
  {
    id: "termination",
    title: "Termination",
    body: [
      "You may stop using the Service and delete your account at any time. We may suspend or terminate your access to the Service at our discretion, with or without notice, if you violate these Terms or if we reasonably believe your use poses a risk to the Service or others. Upon termination, the provisions of these Terms that by their nature should survive will survive, including ownership provisions, disclaimers, and limitations of liability.",
    ],
  },
  {
    id: "disclaimers",
    title: "Disclaimers",
    body: [
      "THE SERVICE IS PROVIDED “AS IS” AND “AS AVAILABLE” WITHOUT WARRANTIES OF ANY KIND, WHETHER EXPRESS, IMPLIED, OR STATUTORY, INCLUDING WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT. AI-GENERATED OUTPUT MAY BE INACCURATE OR INCOMPLETE, AND YOU ARE RESPONSIBLE FOR REVIEWING OUTPUT BEFORE RELYING ON OR PUBLISHING IT.",
    ],
  },
  {
    id: "limitation-of-liability",
    title: "Limitation of Liability",
    body: [
      "TO THE MAXIMUM EXTENT PERMITTED BY LAW, LATTED AND ITS AFFILIATES WILL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS, REVENUE, DATA, OR GOODWILL, ARISING OUT OF OR RELATED TO YOUR USE OF THE SERVICE. IN NO EVENT WILL OUR AGGREGATE LIABILITY EXCEED THE AMOUNTS YOU PAID TO US IN THE TWELVE (12) MONTHS PRECEDING THE EVENT GIVING RISE TO THE CLAIM.",
    ],
  },
  {
    id: "indemnification",
    title: "Indemnification",
    body: [
      "You agree to indemnify and hold harmless Latted and its officers, directors, employees, and agents from any claims, damages, liabilities, and expenses (including reasonable attorneys' fees) arising out of or related to your User Content, your use of the Service, or your violation of these Terms.",
    ],
  },
  {
    id: "governing-law",
    title: "Governing Law",
    body: [
      "These Terms are governed by the laws of the State of Delaware, without regard to its conflict of laws principles. Any disputes arising out of or relating to these Terms or the Service will be resolved in the state or federal courts located in Delaware, and you consent to their jurisdiction.",
    ],
  },
  {
    id: "changes",
    title: "Changes to These Terms",
    body: [
      "We may revise these Terms from time to time. If we make material changes, we will update the date at the top of this page and, where appropriate, provide additional notice through the Service or by email. Your continued use of the Service after changes become effective constitutes acceptance of the revised Terms.",
    ],
  },
  {
    id: "contact",
    title: "Contact Us",
    body: [
      `If you have any questions about these Terms, please contact us at ${LEGAL_CONTACT_EMAIL}.`,
    ],
  },
];

function LegalSectionBody({ section }) {
  return section.body.map((block, i) =>
    typeof block === "string" ? (
      <p key={i} className="legal-page__paragraph">
        {block}
      </p>
    ) : (
      <ul key={i} className="legal-page__list">
        {block.list.map((item) => (
          <li key={item}>{item}</li>
        ))}
      </ul>
    )
  );
}

function LegalPage({ title, titleEm, intro, sections, crossLink }) {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <UniversalLayout>
      <div className="legal-page">
        <header className="legal-page__header">
          <span className="legal-page__label">Legal</span>
          <h1 className="legal-page__title">
            {title} <span className="legal-page__title-em">{titleEm}</span>
          </h1>
          <p className="legal-page__updated">Last updated {LAST_UPDATED}</p>
          <p className="legal-page__intro">{intro}</p>
        </header>

        <article className="legal-page__article">
          <nav className="legal-page__toc" aria-label="Table of contents">
            <span className="legal-page__toc-title">On this page</span>
            <ol className="legal-page__toc-list">
              {sections.map((section) => (
                <li key={section.id}>
                  <a href={`#${section.id}`}>{section.title}</a>
                </li>
              ))}
            </ol>
          </nav>

          {sections.map((section, i) => (
            <section
              key={section.id}
              id={section.id}
              className="legal-page__section"
            >
              <h2 className="legal-page__section-title">
                <span className="legal-page__section-number">
                  {String(i + 1).padStart(2, "0")}
                </span>
                {section.title}
              </h2>
              <LegalSectionBody section={section} />
            </section>
          ))}
        </article>

        <div className="legal-page__cross-link">
          <p>
            Looking for our {crossLink.label}? Read it{" "}
            <Link to={crossLink.to}>here</Link>, or reach us anytime at{" "}
            <a href={`mailto:${LEGAL_CONTACT_EMAIL}`}>{LEGAL_CONTACT_EMAIL}</a>.
          </p>
        </div>
      </div>
    </UniversalLayout>
  );
}

export function PrivacyPolicyPage() {
  return (
    <LegalPage
      title="Privacy"
      titleEm="Policy"
      intro="Your privacy matters to us. This policy explains what information we collect, how we use it, and the choices you have when you create with Latted."
      sections={PRIVACY_SECTIONS}
      crossLink={{ label: "Terms of Service", to: "/terms-of-service" }}
    />
  );
}

export function TermsOfServicePage() {
  return (
    <LegalPage
      title="Terms of"
      titleEm="Service"
      intro="These terms set out the rules for using Latted. We've kept them as clear as possible — please read them carefully before using the Service."
      sections={TERMS_SECTIONS}
      crossLink={{ label: "Privacy Policy", to: "/privacy-policy" }}
    />
  );
}
